import React, { useEffect, useRef, useState } from 'react';
import { FaGoogle, FaStar } from 'react-icons/fa';
import { graphqlRequest } from '../utils/graphqlRequest';
import {
  DEFAULT_REVIEW_MESSAGE,
  DEFAULT_REVIEW_SUBJECT,
  REVIEW_PLACEHOLDERS,
  REVIEW_SETTINGS_QUERY,
  fillReviewTemplate,
  isValidReviewUrl,
} from '../utils/reviewTemplate';

const SAVE_REVIEW_SETTINGS = `
  mutation SaveReviewSettings($input: UpdateCompanyInput!) {
    updateCompany(updateCompanyInput: $input) {
      id
      googleReviewUrl
      reviewRequestSubject
      reviewRequestMessage
    }
  }
`;

const SAMPLE = {
  clientName: 'Sarah',
  service: 'Deep Clean',
  serviceDate: '15 Feb 2024',
};

/** Settings → Reviews: Google review link and the editable review-request message. */
export default function ReviewSettings({ SettingsCard }) {
  const [form, setForm] = useState(null);
  const [companyName, setCompanyName] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const messageRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    graphqlRequest(REVIEW_SETTINGS_QUERY)
      .then((res) => {
        if (cancelled) return;
        const c = res?.getMyProfile?.company || {};
        setCompanyName(c.companyName || '');
        setForm({
          googleReviewUrl: c.googleReviewUrl || '',
          reviewRequestSubject: c.reviewRequestSubject || DEFAULT_REVIEW_SUBJECT,
          reviewRequestMessage: c.reviewRequestMessage || DEFAULT_REVIEW_MESSAGE,
        });
      })
      .catch((err) => {
        if (cancelled) return;
        setMessage({ type: 'error', text: err?.message || 'Failed to load review settings.' });
        setForm({
          googleReviewUrl: '',
          reviewRequestSubject: DEFAULT_REVIEW_SUBJECT,
          reviewRequestMessage: DEFAULT_REVIEW_MESSAGE,
        });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  /** Insert a {{placeholder}} at the cursor in the message. */
  const insertPlaceholder = (key) => {
    const el = messageRef.current;
    const token = `{{${key}}}`;
    const text = form.reviewRequestMessage;
    const start = el ? el.selectionStart : text.length;
    const end = el ? el.selectionEnd : text.length;
    set({ reviewRequestMessage: text.slice(0, start) + token + text.slice(end) });
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(start + token.length, start + token.length);
    });
  };

  const save = async () => {
    const url = form.googleReviewUrl.trim();
    if (url && !isValidReviewUrl(url)) {
      setMessage({ type: 'error', text: 'The Google review link must start with https://' });
      return;
    }
    setSaving(true);
    setMessage({ type: '', text: '' });
    try {
      await graphqlRequest(SAVE_REVIEW_SETTINGS, {
        input: {
          googleReviewUrl: url || null,
          reviewRequestSubject: form.reviewRequestSubject.trim() || null,
          reviewRequestMessage: form.reviewRequestMessage.trim() || null,
        },
      });
      setMessage({ type: 'success', text: 'Review settings saved.' });
    } catch (err) {
      setMessage({ type: 'error', text: err?.message || 'Failed to save review settings.' });
    } finally {
      setSaving(false);
    }
  };

  if (!form) {
    return (
      <div className="st-layout">
        <SettingsCard title="Review settings" icon={<FaStar />}>
          <p className="st-muted">Loading review settings...</p>
        </SettingsCard>
      </div>
    );
  }

  const values = { ...SAMPLE, companyName: companyName || 'Your company' };

  return (
    <div className="st-layout">
      <SettingsCard title="Google reviews" icon={<FaGoogle />}>
        <p className="st-muted st-payments-intro">
          After a client leaves a review here, they are offered this link to post it on your
          Google Business Profile too. Find it in Google Business Profile → <em>Ask for reviews</em>.
        </p>
        <div className="st-integration-form st-integration-form--tight">
          <label className="st-field">
            <span className="st-field-label">Google review link</span>
            <input
              className="st-field-input"
              value={form.googleReviewUrl}
              onChange={(e) => set({ googleReviewUrl: e.target.value })}
              placeholder="https://g.page/r/your-business/review"
              autoComplete="off"
            />
          </label>
        </div>
        {isValidReviewUrl(form.googleReviewUrl) ? (
          <a
            className="st-muted"
            href={form.googleReviewUrl.trim()}
            target="_blank"
            rel="noopener noreferrer"
          >
            Test the link ↗
          </a>
        ) : null}
      </SettingsCard>

      <SettingsCard title="Review request message" icon={<FaStar />}>
        <p className="st-muted st-payments-intro">
          Used every time you request a review. The service and service date are filled in from
          the services you select on the project; you can still edit the message before sending.
        </p>
        <div className="st-integration-form st-integration-form--tight">
          <label className="st-field">
            <span className="st-field-label">Email subject</span>
            <input
              className="st-field-input"
              value={form.reviewRequestSubject}
              onChange={(e) => set({ reviewRequestSubject: e.target.value })}
              maxLength={200}
            />
          </label>
          <label className="st-field">
            <span className="st-field-label">Message</span>
            <textarea
              ref={messageRef}
              className="st-field-input"
              rows={6}
              value={form.reviewRequestMessage}
              onChange={(e) => set({ reviewRequestMessage: e.target.value })}
              maxLength={4000}
            />
          </label>
        </div>
        <div className="st-review-placeholders">
          <span className="st-muted">Insert:</span>
          {REVIEW_PLACEHOLDERS.map((p) => (
            <button
              key={p.key}
              type="button"
              className="acct-btn-secondary"
              onClick={() => insertPlaceholder(p.key)}
            >
              {p.label}
            </button>
          ))}
          <button
            type="button"
            className="acct-btn-secondary"
            onClick={() =>
              set({
                reviewRequestSubject: DEFAULT_REVIEW_SUBJECT,
                reviewRequestMessage: DEFAULT_REVIEW_MESSAGE,
              })
            }
          >
            Reset to default
          </button>
        </div>

        <div className="st-review-preview">
          <span className="st-field-label">Preview</span>
          <strong>{fillReviewTemplate(form.reviewRequestSubject, values)}</strong>
          <p>{fillReviewTemplate(form.reviewRequestMessage, values)}</p>
        </div>

        {message.text ? (
          <p className={`st-message st-message--${message.type}`}>{message.text}</p>
        ) : null}
        <div className="st-form-actions st-form-actions--left">
          <button type="button" className="acct-btn-primary" onClick={save} disabled={saving}>
            {saving ? 'Saving...' : 'Save review settings'}
          </button>
        </div>
      </SettingsCard>
    </div>
  );
}
